/**
 * ReturnsHistogram — distribution of individual trade returns (pnl_pct),
 * split winners (green) vs losers (red), with a risk/reward summary.
 *
 * Props:
 *   data    from analytics.returnsHistogram(trades)
 *           {buckets:[{label, win, loss}], avgWin, avgLoss, nWins, nLosses}
 *   height  number  SVG height (default 220)
 */

import { Bar, BarChart, Cell, Tooltip, XAxis, YAxis } from "recharts";

export default function ReturnsHistogram({ data, height = 220 }) {
  if (!data || !data.buckets?.length) return null;
  const { buckets, avgWin, avgLoss, nWins, nLosses } = data;

  const chartdata = buckets.map((b)=> {
    return {
      ...b,
      'isNeg': b.label.startsWith('<') || b.label.startsWith('-'),
      'value': b.loss > 0 ? b.loss : b.win > 0 ? b.win : null,
    }
  })

  return (
    <div className="flex flex-col gap-3">
      {/* Risk / reward profile */}
      <div className="flex flex-wrap gap-4 text-xs">
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Avg Winner</em>
          <strong className="text-win">+{avgWin.toFixed(1)}%</strong>
        </span>
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Avg Loser</em>
          <strong className="text-loss">{avgLoss.toFixed(1)}%</strong>
        </span>
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Win / Loss</em>
          <strong className="text-foreground">{nWins} / {nLosses}</strong>
        </span>
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Reward:Risk</em>
          <strong className="text-foreground">{avgLoss ? (Math.abs(avgWin / avgLoss)).toFixed(2) : '—'}</strong>
        </span>
      </div>

      <BarChart
        className="text-sm w-full"
        style={{height: `${height}px` }}
        responsive
        margin={{
          top: 20,
          right: 0,
          left: 0,
          bottom: 5,
        }}
        data={chartdata}
        stackOffset="sign"
      >
        <Tooltip 
          labelStyle={{ color: 'hsl(var(--foreground))' }}
          contentStyle={{background: 'hsl(var(--background))', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
        />
        
        <XAxis dataKey="label" niceTicks="snap125" 
          tick={{fill: 'hsl(var(--foreground))'}}
        />
        <YAxis width="auto" niceTicks="snap125"
          domain={[0, 'dataMax']}
          tick={{fill: 'hsl(var(--foreground))'}}
        />
        <Bar dataKey='value'>
          {chartdata.map((entry, index) => {
            const barColor = entry.isNeg ? 'hsl(var(--loss))' : 'hsl(var(--win))';
            return (
              <Cell 
                key={`cell-${index}`} 
                fill={barColor} 
              />
            );
          })}
        </Bar>
      </BarChart>

      <p className="text-[10px] text-muted-foreground">Trade returns bucketed by P&amp;L %. Green = winners, red = losers.</p>
    </div>
  );
}
